import apiClient from '../clients/apiClient';
import { ApiResponse } from '../schemas/apiResponseSchema';
import { Schemas } from '../types/api-types';

export type LoanProductResponse = Schemas['LoanProductResponse'];
export type LoanApplicationResponse = Schemas['LoanApplicationResponse'];
export type PlLoanAmotResponse = Schemas['PlLoanAmotResponse'];

export interface ActiveBranch {
    branch_id: number;
    branch_name: string;
}

/**
 * Fetch all available loan products
 * @returns Array of loan products
 */
export const getLoanProducts = async (): Promise<LoanProductResponse[]> => {
    try {
        const response: ApiResponse<LoanProductResponse[]> = (await apiClient.get("/loan-application/products")).data;
        return response.data || [];
    } catch (error: any) {
        throw new Error(error.response?.data?.detail || "Failed to fetch loan products");
    }
};

/**
 * Fetch all available FD advance loan products
 * @returns Array of FD advance loan products
 */
export const getFdAdvanceProducts = async (): Promise<LoanProductResponse[]> => {
    try {
        const response: ApiResponse<LoanProductResponse[]> = (await apiClient.get("/loan-application/fd-products")).data;
        return response.data || [];
    } catch (error: any) {
        throw new Error(error.response?.data?.detail || "Failed to fetch FD advance products");
    }
};

/**
 * Fetch active branches (status = 1)
 * @returns Array of active branches
 */
export const getActiveBranches = async (): Promise<ActiveBranch[]> => {
    try {
        const response: ApiResponse<ActiveBranch[]> = (await apiClient.get("/branches/active")).data;
        return response.data || [];
    } catch (error: any) {
        throw new Error(error.response?.data?.detail || "Failed to fetch branches");
    }
};

/**
 * Create a new loan application
 * @param customerNumber - Customer number or NIC
 * @param loanProductId - ID of the loan product
 * @param requestedAmount - Requested loan amount
 * @param branchId - Branch ID for the application
 * @returns Created loan application details
 */
export const createLoanApplication = async (
    customerNumber: string,
    loanProductId: number,
    requestedAmount: number,
    branchId?: number
): Promise<LoanApplicationResponse> => {
    try {
        const response: ApiResponse<LoanApplicationResponse> = (await apiClient.post("/loan-application/create", {
            customer_number: customerNumber,
            loan_product_id: loanProductId,
            requested_amount: requestedAmount,
            branch_id: branchId,
        })).data;
        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.detail || "Failed to create loan application");
    }
};

export const createLoanAmotSchedule = async (
    loanProductId: number | null,
    amount: number,
    interestRate: number,
    loanPeriod: number,
    disburseDate: string,
    installmentAmount?: number,
    extras?: { interest_policy_id?: number | null; period_policy?: number | null }
): Promise<PlLoanAmotResponse[]> => {
    try {
        const payload: any = {
            loan_product_id: loanProductId,
            amount,
            interest_rate: interestRate,
            loan_period: loanPeriod,
            disburse_date: disburseDate,
            installment_amount: installmentAmount,
        };
        if (extras) {
            if (extras.interest_policy_id != null) payload.interest_policy_id = extras.interest_policy_id;
            if (extras.period_policy != null) payload.period_policy = extras.period_policy;
        }

        const response: ApiResponse<PlLoanAmotResponse[]> = (await apiClient.post("/loan-application/amot_schedule", payload)).data;
        return response.data;
    }
    catch (error: any) {
        throw new Error(error.response?.data?.detail || "Failed to create loan amortization schedule");
    }
};

export interface LoanGuarantor {
    guarantor_type_id: number;
    guarantor_type: string | null;
    customer_number: string | null;
    customer_name: string | null;
    exists: boolean;
}

export interface CustomerLoanItem {
    loan_id: number;
    loan_account_number: string | null;
    product_name: string | null;
    pl_operating_type_id: number | null;
    balance: number | null;
    issue_date: string | null;
    issued_amount: number | null;
    period: number | null;
    interest_rate: number | null;
    installment: number | null;
    transaction_date: string | null;
    last_transaction_date: string | null;
    past_due: number | null;
    past_due_amount: number | null;
    interest: number | null;
    due_interest: number | null;
    guarantor_count: number;
    max_guarantors: number;
    guarantors: LoanGuarantor[];
}

export interface CustomerGuarantorItem {
    loan_id: number;
    loan_account_number: string | null;
    product_name: string | null;
    balance: number | null;
    issue_date: string | null;
    issued_amount: number | null;
    period: number | null;
    interest_rate: number | null;
    installment: number | null;
    transaction_date: string | null;
    last_transaction_date: string | null;
    past_due: number | null;
    past_due_amount: number | null;
    interest: number | null;
    due_interest: number | null;
    borrower_customer_number?: string | null;
    borrower_customer_name?: string | null;
    guarantor: {
        guarantor_type_id: number | null;
        guarantor_type: string | null;
        customer_number: string | null;
        customer_name: string | null;
    };
}

export interface CustomerLoanSummary {
    customer_number: string;
    summary: {
        my_loans_count: number;
        guarantor_loans_count: number;
        max_guarantors: number;
    };
    my_loans: CustomerLoanItem[];
    i_am_guarantor_for: CustomerGuarantorItem[];
}

export async function getCustomerLoanSummary(
    customerNumber: string
): Promise<CustomerLoanSummary> {
    try {
        const response: ApiResponse<CustomerLoanSummary> = (
            await apiClient.get(`/loan-application/customer-summary/${customerNumber}`)
        ).data;

        return response.data;
    } catch (error: any) {
        throw error;
    }
}